// ────────────────────────────────────────────────────────────────
// src/components/AppSidebar.js
// ────────────────────────────────────────────────────────────────
import React from 'react'
import { Link, useNavigate } from 'react-router-dom'

function AppSidebar({ onNavLinkClick, lang = 'en' }) {
  /* ------------------------------------------------------------------ *
     User & Onboarding flag (from localStorage)
   * ------------------------------------------------------------------ */
  const storedUser  = JSON.parse(localStorage.getItem('user'))
  const userName    = storedUser ? storedUser.name : 'Guest'

  /**
   * isOnboarded
   *   • We now treat the user as onboarded **only** when the DB flag
   *     is explicitly true.
   */
  const isOnboarded = storedUser?.onboarding_complete === true  // ★ UPDATED

  const nav = useNavigate()

  /* ------------------------------------------------------------------ *
     Minimal translations for the sidebar
   * ------------------------------------------------------------------ */
  const translations = {
    en: {
      title: 'PayMe',
      dashboard: 'Dashboard',
      sendMoney: 'Send Money',
      requestMoney: 'Request Money',
      pending: 'Pending Requests',
      history: 'Transaction History',
      social: 'Social',
      loggedInAs: 'Logged in as',
      myPayouts: 'My Payouts',
    },
    ja: {
      title: 'ペイミ',
      dashboard: 'ダッシュボード',
      sendMoney: '送金',
      requestMoney: '支払い依頼',
      pending: '保留中のリクエスト',
      history: '取引履歴',
      social: 'ソーシャル',
      loggedInAs: 'ログイン中：',
      myPayouts: 'マイペイアウト',
    },
    pt: {
      title: 'PayMe',
      dashboard: 'Painel',
      sendMoney: 'Enviar Dinheiro',
      requestMoney: 'Solicitar Dinheiro',
      pending: 'Solicitações Pendentes',
      history: 'Histórico de Transações',
      social: 'Social',
      loggedInAs: 'Conectado como:',
      myPayouts: 'Meus Pagamentos',
    },
  }

  const t = translations[lang] || translations.en

  /* ------------------------------------------------------------------ *
     Guard helper → greys-out & intercepts clicks when !isOnboarded
   * ------------------------------------------------------------------ */
  const guard = (e, path) => {
    if (!isOnboarded && path !== '/') {
      e.preventDefault()       // cancel navigation
      nav('/')                 // bounce back to dashboard
    }
    onNavLinkClick?.()         // still close drawer on mobile
  }

  /* ------------------------------------------------------------------ *
     JSX
   * ------------------------------------------------------------------ */
  return (
    <div className="h-full flex flex-col bg-gray-800 text-white">
      {/* Logo / Title */}
      <div className="py-4 px-4 text-xl font-bold border-b border-gray-700">
        {t.title}
      </div>

      {/* Navigation links */}
      <nav className="mt-4 flex-1 overflow-y-auto">
        <ul className="space-y-2 px-4">
          {/* Dashboard – always enabled */}
          <li>
            <Link
              to="/"
              onClick={(e) => guard(e, '/')}
              className="block px-2 py-2 rounded hover:bg-gray-700"
            >
              {t.dashboard}
            </Link>
          </li>

          {/* Send Money */}
          <li>
            <Link
              to="/send-money"
              onClick={(e) => guard(e, '/send-money')}
              className={`block px-2 py-2 rounded ${
                isOnboarded
                  ? 'hover:bg-gray-700'
                  : 'opacity-40 cursor-not-allowed'
              }`}
            >
              {t.sendMoney}
            </Link>
          </li>

          {/* Request Money */}
          <li>
            <Link
              to="/request-money"
              onClick={(e) => guard(e, '/request-money')}
              className={`block px-2 py-2 rounded ${
                isOnboarded
                  ? 'hover:bg-gray-700'
                  : 'opacity-40 cursor-not-allowed'
              }`}
            >
              {t.requestMoney}
            </Link>
          </li>

          {/* Social */}
          <li>
            <Link
              to="/social"
              onClick={(e) => guard(e, '/social')}
              className={`block px-2 py-2 rounded ${
                isOnboarded
                  ? 'hover:bg-gray-700'
                  : 'opacity-40 cursor-not-allowed'
              }`}
            >
              {t.social}
            </Link>
          </li>

          {/* Pending Requests */}
          <li>
            <Link
              to="/pending-requests"
              onClick={(e) => guard(e, '/pending-requests')}
              className={`block px-2 py-2 rounded ${
                isOnboarded
                  ? 'hover:bg-gray-700'
                  : 'opacity-40 cursor-not-allowed'
              }`}
            >
              {t.pending}
            </Link>
          </li>

          {/* Transaction History */}
          <li>
            <Link
              to="/history"
              onClick={(e) => guard(e, '/history')}
              className={`block px-2 py-2 rounded ${
                isOnboarded
                  ? 'hover:bg-gray-700'
                  : 'opacity-40 cursor-not-allowed'
              }`}
            >
              {t.history}
            </Link>
          </li>

          {/* My Payouts */}
          <li>
            <Link
              to="/my-payouts"
              onClick={(e) => guard(e, '/my-payouts')}
              className={`block px-2 py-2 rounded ${
                isOnboarded
                  ? 'hover:bg-gray-700'
                  : 'opacity-40 cursor-not-allowed'
              }`}
            >
              {t.myPayouts}
            </Link>
          </li>
        </ul>
      </nav>

      {/* Footer user info */}
      <div className="mt-auto py-4 px-4 border-t border-gray-700 text-center">
        <p className="mb-2 text-sm text-gray-300">{t.loggedInAs}</p>
        <p className="font-semibold">{userName}</p>
      </div>
    </div>
  )
}

export default AppSidebar
